import {
  Check,
  Clipboard,
  Download,
  Hash,
  Heading1,
  Image,
  LayoutGrid,
  MessageCircleMore,
  MousePointerClick,
  Sparkles,
  SquarePen,
  Type,
  X,
} from 'lucide-react'
import {
  sectionSupportsCopy,
  sectionSupportsDownload,
  type MobileSelectedSection,
} from './mobile-selection-types'
import { getSectionAskActionLabel, getSectionEditActionLabel } from '../utils/carousel-slide-focus'
import { useComposerReadOnly } from './composer-read-only'

const SECTION_TYPE_ICONS: Record<string, React.FC<{ size?: number; className?: string }>> = {
  text: Type,
  image: Image,
  cta: MousePointerClick,
  hero: Sparkles,
  header: Heading1,
  columns: LayoutGrid,
  caption: Type,
  hashtags: Hash,
  headline: Type,
  subtitle: Type,
  tagline: Type,
}

interface MobileActionBarProps {
  selection: MobileSelectedSection
  successAction: 'copy' | 'download' | null
  onAskAI: () => void
  onEdit: () => void
  onCopy: () => void
  onDownload: () => void
  onClose: () => void
}

function getSectionChipLabel(selection: MobileSelectedSection): string {
  // Just the label is enough (Image, Caption, Hashtags, etc.)
  return selection.label
}

export function MobileActionBar({
  selection,
  successAction,
  onAskAI,
  onEdit,
  onCopy,
  onDownload,
  onClose,
}: MobileActionBarProps) {
  const readOnly = useComposerReadOnly()
  const showCopy = sectionSupportsCopy(selection)
  const showDownload = sectionSupportsDownload(selection)
  const SectionIcon = SECTION_TYPE_ICONS[selection.sectionType]
  const askLabel = getSectionAskActionLabel(selection.sectionId)
  const editLabel = getSectionEditActionLabel(selection.sectionId)

  return (
    <div className="absolute inset-x-0 bottom-0 z-30 animate-in duration-200 fade-in slide-in-from-bottom-4">
      <div className="rounded-t-[16px] border-t border-line bg-bg-2 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)] shadow-[var(--shadow-card)]">
        {/* Section indicator + close — acid chip echoes the SectionContextChip
            in the desktop chat input so the same selection reads as the same
            entity across device sizes. */}
        <div className="mb-2 flex items-center gap-2">
          <div className="bg-acid-tint flex min-w-0 flex-1 items-center gap-1.5 rounded-[6px] border border-acid px-2.5 py-1.5">
            {SectionIcon && <SectionIcon size={13} className="flex-shrink-0 text-acid" />}
            <span className="mono truncate text-[10px] tracking-[0.15em] text-acid uppercase">
              {getSectionChipLabel(selection)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-3 text-paper-dim active:bg-bg-4"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Action rows. A live post is view-only — only Copy/Download remain;
            Ask AI + Edit are dropped. */}
        <div className="flex flex-col gap-0.5">
          {!readOnly && (
            <>
              <ActionRow
                icon={<MessageCircleMore size={16} />}
                label={askLabel}
                onClick={onAskAI}
              />
              <ActionRow icon={<SquarePen size={16} />} label={editLabel} onClick={onEdit} />
            </>
          )}
          {showCopy && (
            <ActionRow
              icon={
                successAction === 'copy' ? (
                  <Check size={16} className="text-success" />
                ) : (
                  <Clipboard size={16} />
                )
              }
              label={successAction === 'copy' ? 'Copied!' : 'Copy'}
              onClick={onCopy}
              success={successAction === 'copy'}
            />
          )}
          {showDownload && (
            <ActionRow
              icon={
                successAction === 'download' ? (
                  <Check size={16} className="text-success" />
                ) : (
                  <Download size={16} />
                )
              }
              label={successAction === 'download' ? 'Downloaded!' : 'Download'}
              onClick={onDownload}
              success={successAction === 'download'}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function ActionRow({
  icon,
  label,
  onClick,
  success,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  success?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-paper transition-colors active:bg-bg-3"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line bg-bg-3 text-paper-dim">
        {icon}
      </span>
      <span className={`text-[14px] font-medium ${success ? 'text-success' : 'text-paper'}`}>
        {label}
      </span>
    </button>
  )
}
